import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RequestService, Request } from '../../services/request.service';
import { SystemService, SystemStatus } from '../../services/system.service';
import { environment } from '../../../environments/environment';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { RequestListComponent } from '../request-list/request-list.component';
import { RequestDetailComponent } from '../request-detail/request-detail.component';
import { DialogComponent, DialogConfig } from '../dialog/dialog.component';
import { interval, Subscription, of, EMPTY, timer, forkJoin } from 'rxjs';
import { switchMap, catchError, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-captain-panel',
  standalone: true,
  imports: [CommonModule, SidebarComponent, RequestListComponent, RequestDetailComponent, DialogComponent],
  templateUrl: './captain-panel.component.html',
  styleUrls: ['./captain-panel.component.scss']
})
export class CaptainPanelComponent implements OnInit, OnDestroy {
  waitingRequests: Request[] = [];
  selectedRequest: Request | null = null;
  /** Talep timeout süresi (dakika) – liste geri sayımı için kullanılır */
  requestTimeoutMinutes: number = 30;
  systemStatus: SystemStatus = 'open';
  /** Çalışma saatleri içinde miyiz (frontend tarafında hesaplanır) */
  private isWithinWorkingHours: boolean = true;
  currentView: 'requests' | 'history' = 'requests';
  historyRequests: Request[] = [];
  isLoadingRequests: boolean = false;
  isLoadingHistory: boolean = false;
  isProcessingResponse: boolean = false;
  private refreshSubscription?: Subscription;
  /** visibilitychange dinleyicisi - sekme tekrar görünür olunca yenilemek için */
  private visibilityListener = () => this.onVisibilityChange();
  /** Önceki bekleyen talep sayısı; yeni talep geldiğinde ses çalmak için kullanılır */
  private previousWaitingCount = 0;
  showDialog: boolean = false;
  dialogConfig: DialogConfig = {
    title: '',
    message: '',
    type: 'error'
  };
  /** Mobilde menü (drawer) açık mı */
  mobileMenuOpen = false;

  /** Viewport mobil genişlikte mi (≤767px); otomatik seçim ve detay paneli için */
  get isMobileView(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 767;
  }
  /** Bildirim sesi – kullanıcı etkileşimi sonrası çalabilmesi için tek öğe kullanılıyor */
  private notificationAudio: HTMLAudioElement | null = null;
  /** Tarayıcı ses politikası: en az bir kez kullanıcı tıklamasıyla “açıldı” mı */
  private audioUnlocked = false;

  constructor(
    private requestService: RequestService,
    private systemService: SystemService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    console.log('🔵 CaptainPanel ngOnInit çağrıldı');
    this.verifyAuthentication();
    this.loadTimeoutSettings();
    // Sistem durumunu ve çalışma saatlerini yükle
    this.loadSystemStatus(() => {
      if (!this.canSendRequests()) {
        console.log('⛔ Sistem kapalı veya çalışma saatleri dışında, bekleyen talepler yüklenmeyecek. Geçmiş yine de yüklenecek.');
        // Sistem kapalıyken de geçmiş talepler görülebilsin
        this.loadHistory();
      } else {
        // Sayfa açılır açılmaz hem beklemedeki talepler hem de geçmiş talepler yüklenir
        console.log('🔵 İlk veri yükleme başlıyor (sistem açık ve çalışma saatleri içinde)...');
        this.loadWaitingRequests();
        this.loadHistory();

        // Otomatik yenilemeyi başlatıyoruz
        this.startAutoRefresh();
      }
    });
    // Sekme tekrar görünür olunca bir kerelik yenile (çok sekmeli kullanımda gereksiz istek azalır)
    document.addEventListener('visibilitychange', this.visibilityListener);
  }

  verifyAuthentication(): void {
    // Token will be verified by interceptor, but we can add additional check here if needed
    console.log('Captain panel initialized');
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
    document.removeEventListener('visibilitychange', this.visibilityListener);
  }

  /** Sekme görünür/arka plan değişince: görünür olunca bir kerelik yenile */
  private onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.loadSystemStatus(() => {
        if (!this.canSendRequests()) {
          console.log('⛔ Sekme görünür oldu ama sistem kapalı veya çalışma saatleri dışında, bekleyen talepler yenilenmiyor. Geçmiş yine de yenilenecek.');
          if (this.currentView === 'history') {
            this.loadHistory();
          }
          return;
        }
        if (this.currentView === 'requests') {
          this.loadWaitingRequests();
        } else {
          this.loadHistory();
        }
      });
    }
  }

  /** Şu an istek atmaya izin var mı? (sistem açık + çalışma saatleri içinde) */
  private canSendRequests(): boolean {
    return this.isSystemOpen && this.isWithinWorkingHours;
  }

  /** Sistem KAPALI mı değil mi – OPEN ve BUSY için true, CLOSED için false */
  get isSystemOpen(): boolean {
    return this.systemStatus !== 'closed';
  }

  /** "HH:mm" aralığına göre şu an çalışma saatleri içinde miyiz? */
  private calculateWithinWorkingHours(workingStart?: string, workingEnd?: string): boolean {
    if (!workingStart || !workingEnd) {
      // Çalışma saatleri ayarlı değilse her zaman açık kabul et
      return true;
    }

    const [sh, sm] = workingStart.split(':').map(Number);
    const [eh, em] = workingEnd.split(':').map(Number);
    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;

    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    // Geceyi aşan aralık (örn. 22:00 - 02:00)
    if (endMinutes <= startMinutes) {
      return nowMinutes >= startMinutes || nowMinutes < endMinutes;
    }

    return nowMinutes >= startMinutes && nowMinutes < endMinutes;
  }

  loadSystemStatus(onLoaded?: () => void): void {
    this.systemService.getSystemStatus().subscribe({
      next: (status) => {
        const resolvedStatus: SystemStatus =
          status.status ||
          (status.is_open ? 'open' : 'closed');
        this.systemStatus = resolvedStatus;
        this.isWithinWorkingHours = this.calculateWithinWorkingHours(
          status.working_hours_start,
          status.working_hours_end
        );
        console.log('🔧 System status yüklendi:', {
          status: this.systemStatus,
          is_open: this.isSystemOpen,
          working_hours_start: status.working_hours_start,
          working_hours_end: status.working_hours_end,
          isWithinWorkingHours: this.isWithinWorkingHours
        });
        if (onLoaded) {
          onLoaded();
        }
      },
      error: (error) => {
        console.error('Error loading system status:', error);
      }
    });
  }

  /** Profildeki "Talep Timeout Süresi" ayarını yükler ve liste geri sayımında kullanır */
  loadTimeoutSettings(): void {
    this.systemService.getTimeoutSettings().subscribe({
      next: (settings) => {
        this.requestTimeoutMinutes = settings.request_timeout_minutes || 30;
      },
      error: (error) => {
        console.error('Timeout ayarı yüklenemedi:', error);
        this.requestTimeoutMinutes = 30;
      }
    });
  }

  loadWaitingRequests(): void {
    console.log('🟢 loadWaitingRequests çağrıldı');
    this.isLoadingRequests = true;
    this.cdr.detectChanges(); // Loading state değişikliğini hemen yansıt
    this.requestService.getWaitingRequests()
      .pipe(
        finalize(() => {
          // Her durumda (başarılı veya hatalı) loading'i kapat
          this.isLoadingRequests = false;
          console.log('🟢 loadWaitingRequests tamamlandı, loading kapatıldı');
          console.log('🟢 isLoadingRequests değeri:', this.isLoadingRequests);
          this.cdr.detectChanges(); // Change detection'ı manuel tetikle
        })
      )
      .subscribe({
        next: (requests) => {
          console.log('✅ Talepler başarıyla yüklendi:', requests);
          console.log('📊 Talep sayısı:', requests?.length || 0);
          this.waitingRequests = requests || [];
          this.previousWaitingCount = this.waitingRequests.length;
          // Masaüstünde seçili yoksa ilk talebi seç; mobilde kullanıcı listeden seçsin
          if (this.waitingRequests.length > 0 && !this.selectedRequest && !this.isMobileView) {
            this.selectRequest(this.waitingRequests[0]);
          }
        },
        error: (error) => {
          console.error('❌ Talep yükleme hatası:', error);
          console.error('❌ Hata detayları:', error.error);
          console.error('❌ HTTP Status:', error.status);
          this.waitingRequests = [];
        }
      });
  }

  loadHistory(): void {
    this.isLoadingHistory = true;
    this.cdr.detectChanges(); // Loading state değişikliğini hemen yansıt
    this.requestService.getHistory()
      .pipe(
        finalize(() => {
          // Her durumda (başarılı veya hatalı) loading'i kapat
          this.isLoadingHistory = false;
          console.log('🟢 loadHistory tamamlandı, loading kapatıldı');
          console.log('🟢 isLoadingHistory değeri:', this.isLoadingHistory);
          this.cdr.detectChanges(); // Change detection'ı manuel tetikle
        })
      )
      .subscribe({
        next: (requests) => {
          this.historyRequests = requests || [];
        },
        error: (error) => {
          console.error('Error loading history:', error);
          this.historyRequests = [];
        }
      });
  }

  startAutoRefresh(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }

    // Bekleyen talepleri her 30 saniyede bir kontrol et.
    // Sadece sekme görünür VE odak bu sekmedeyse istek atıyoruz; böylece N sekme = 1 istek (odaklı olan).
    this.refreshSubscription = timer(30000, 30000)
      .pipe(
        switchMap(() => {
          // Sistem kapalıysa veya çalışma saatleri dışındaysa hiç istek atma
          if (!this.canSendRequests()) {
            console.log('⛔ Auto-refresh sırasında sistem kapalı veya çalışma saatleri dışında, istek atılmıyor.');
            return of({ waiting: null, history: null });
          }
          const visible = typeof document !== 'undefined' && document.visibilityState === 'visible';
          const focused = typeof document !== 'undefined' && document.hasFocus();
          if (!visible || !focused) {
            return of({ waiting: null, history: null });
          }
          const waitingRequests$ = this.requestService.getWaitingRequests().pipe(
            catchError(error => {
              console.error('Auto-refresh waiting requests error:', error);
              return of([]);
            })
          );

          if (this.currentView === 'requests') {
            return waitingRequests$.pipe(
              switchMap(waitingRequests => of({ waiting: waitingRequests, history: null }))
            );
          } else if (this.currentView === 'history') {
            const historyRequests$ = this.requestService.getHistory().pipe(
              catchError(error => {
                console.error('Auto-refresh history error:', error);
                return of([]);
              })
            );
            return forkJoin({
              waiting: waitingRequests$,
              history: historyRequests$
            });
          }
          return of({ waiting: null, history: null });
        })
      )
      .subscribe({
        next: (data) => {
          // Bekleyen talepleri her zaman güncelle (sidebar badge için)
          if (data.waiting !== null) {
            const newWaitingCount = (data.waiting || []).length;
            if (newWaitingCount > this.previousWaitingCount) {
              this.playNewRequestSound();
            }
            this.previousWaitingCount = newWaitingCount;
            console.log('🔄 Auto-refresh: Bekleyen talep sayısı:', newWaitingCount);
            this.waitingRequests = data.waiting || [];
            console.log('🔄 waitingRequests.length:', this.waitingRequests.length);
            this.cdr.detectChanges();
            // Masaüstünde seçili yoksa ilk talebi seç; mobilde otomatik seçim yok
            if (this.currentView === 'requests' && !this.isMobileView) {
              if (this.waitingRequests.length > 0 && !this.selectedRequest) {
                this.selectRequest(this.waitingRequests[0]);
              }
            }
          }

          // Geçmiş sayfasındayken geçmiş talepleri güncelle
          if (this.currentView === 'history' && data.history !== null) {
            this.historyRequests = data.history || [];
            this.cdr.detectChanges();
          }
        }
      });
  }

  selectRequest(request: Request): void {
    this.selectedRequest = request;
  }

  isLoading(): boolean {
    return (this.currentView === 'requests' && this.isLoadingRequests) ||
      (this.currentView === 'history' && this.isLoadingHistory);
  }

  onSystemStatusChange(newStatus: SystemStatus): void {
    const oldStatus = this.systemStatus;

    // Optimistic update - immediately update UI
    this.systemStatus = newStatus;

    // Then update on server
    this.systemService.updateSystemStatus(newStatus).subscribe({
      next: () => {
        // Success - status already updated
        console.log(`System status updated to: ${newStatus.toUpperCase()}`);
        // Reload data after status change
        if (this.currentView === 'requests') {
          this.loadWaitingRequests();
        } else {
          this.loadHistory();
        }
      },
      error: (error) => {
        console.error('Error updating system status:', error);
        console.error('Error details:', error.error);
        // Revert on error
        this.systemStatus = oldStatus;
        const errorMessage = error.error?.error || error.message || 'Bilinmeyen hata';
        this.showErrorDialog(`Sistem durumu güncellenirken bir hata oluştu: ${errorMessage}`);
      }
    });
  }

  onQuickResponse(minutes: number): void {
    if (!this.selectedRequest) return;

    // Sistem kapalıysa işlem yapma
    if (!this.isSystemOpen) {
      alert('Sistem kapalı olduğu için talep onaylama mümkün değildir.');
      return;
    }

    this.isProcessingResponse = true; // Button disabled immediately
    this.cdr.detectChanges(); // Update UI immediately

    this.requestService.sendQuickResponse(
      this.selectedRequest.id, 
      minutes, 
      this.selectedRequest.phone_number
    )
      .pipe(
        finalize(() => {
          this.isProcessingResponse = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => {
          this.loadWaitingRequests();
          this.selectedRequest = null;
        },
        error: (error) => {
          console.error('Error sending response:', error);
          const errorMessage = error.error?.error || error.message || 'Yanıt gönderilirken bir hata oluştu. Lütfen tekrar deneyin.';
          alert(errorMessage);
        }
      });
  }

  navigateToRequests(): void {
    this.currentView = 'requests';
    this.selectedRequest = null;
    this.mobileMenuOpen = false;
    if (this.canSendRequests()) {
      this.loadWaitingRequests();
      this.startAutoRefresh();
    } else {
      console.log('⛔ navigateToRequests: Sistem kapalı veya çalışma saatleri dışında, istek atılmıyor.');
    }
  }

  navigateToHistory(): void {
    this.currentView = 'history';
    this.selectedRequest = null;
    this.mobileMenuOpen = false;
    // Geçmiş sayfasına geçerken, sistem kapalı olsa bile geçmişi her zaman yükle
    this.loadHistory();
    // Bekleyen talepler ve auto-refresh sadece sistem açık ve çalışma saatleri içindeyken çalışsın
    if (this.canSendRequests()) {
      this.loadWaitingRequests();
      this.startAutoRefresh();
    } else {
      console.log('⛔ navigateToHistory: Sistem kapalı veya çalışma saatleri dışında, sadece geçmiş yüklendi.');
    }
  }

  showErrorDialog(message: string): void {
    this.dialogConfig = {
      title: 'Hata',
      message: message,
      type: 'error',
      confirmText: 'Tamam',
      showCancel: false
    };
    this.showDialog = true;
  }

  onDialogClose(): void {
    this.showDialog = false;
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  /** Mobilde detay ekranından listeye dön */
  onBackFromDetail(): void {
    this.selectedRequest = null;
  }

  /**
   * Kaptan panelinde ilk tıklama/t dokunmada sesi “açmak” için.
   * Tarayıcılar kullanıcı etkileşimi olmadan ses çalmayı engeller.
   */
  unlockAudioOnce(): void {
    if (this.audioUnlocked) return;
    const soundUrl = `${environment.apiUrl}/assets/lacivert-tekne-bildirim.mp3`;
    const audio = new Audio(soundUrl);
    this.notificationAudio = audio;
    audio.volume = 0;
    audio.play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 1;
        this.audioUnlocked = true;
      })
      .catch(() => {});
  }

  /** Yeni talep geldiğinde kaptanı uyarmak için bildirim sesini çalar */
  private playNewRequestSound(): void {
    const soundUrl = `${environment.apiUrl}/assets/lacivert-tekne-bildirim.mp3`;
    if (this.notificationAudio && this.audioUnlocked) {
      this.notificationAudio.currentTime = 0;
      this.notificationAudio.volume = 1;
      this.notificationAudio.play().catch(() => {});
      return;
    }
    const audio = new Audio(soundUrl);
    audio.play().catch(() => {});
  }
}

